/**
 * Parental Consent Page
 *
 * Parent clicks the consent link in their email — this page calls
 * GET /auth/parental-consent/verify?token=... and shows one of three states:
 *  - verifying (spinner)
 *  - success   (consent granted, child account now active)
 *  - error     (token expired / mismatched / tampered)
 *
 * Public route — the token itself is the authenticator; the parent is not
 * required to be logged in.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthLayout from '../../components/auth/AuthLayout';
import Button from '../../components/auth/Button';
import Alert from '../../components/auth/Alert';
import { verifyConsentToken, VerifyConsentResponse } from '../../services/parentalConsentService';
import { ApiError } from '../../types/auth';

type Status = 'verifying' | 'success' | 'error' | 'no-token';

const ParentalConsentPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'no-token');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<VerifyConsentResponse | null>(null);
  const hasVerified = useRef(false);

  useEffect(() => {
    if (!token) return;
    if (hasVerified.current) return;
    hasVerified.current = true;

    verifyConsentToken(token)
      .then((res) => {
        setResult(res);
        setStatus('success');
      })
      .catch((err: ApiError) => {
        setErrorMessage(err.message || t('auth:parentalConsent.errorGeneric'));
        setStatus('error');
      });
  }, [token, t]);

  if (status === 'verifying') {
    return (
      <AuthLayout
        title={t('auth:parentalConsent.verifyingTitle')}
        subtitle={t('auth:parentalConsent.verifyingSubtitle')}
      >
        <div className="flex items-center justify-center py-8">
          <svg
            className="animate-spin h-12 w-12 text-primary-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </AuthLayout>
    );
  }

  if (status === 'success' && result) {
    return (
      <AuthLayout
        title={t('auth:parentalConsent.successTitle')}
        subtitle={t('auth:parentalConsent.successSubtitle', { child: result.childName })}
      >
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('auth:parentalConsent.successHeading', { child: result.childName })}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('auth:parentalConsent.successBody')}
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-left text-sm text-amber-900 mb-4">
              <p className="font-semibold mb-2">{t('auth:parentalConsent.rulesHeading')}</p>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('auth:parentalConsent.ruleNoPersonalisedAds')}</li>
                <li>{t('auth:parentalConsent.ruleNoThirdPartySharing')}</li>
                <li>{t('auth:parentalConsent.ruleNoVoiceRetention')}</li>
              </ul>
            </div>
          </div>

          <Link to="/account/parental-controls">
            <Button fullWidth>{t('auth:parentalConsent.openDashboard')}</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // error or no-token
  return (
    <AuthLayout
      title={t('auth:parentalConsent.errorTitle')}
      subtitle={t('auth:parentalConsent.errorSubtitle')}
    >
      {errorMessage && (
        <Alert type="error" onDismiss={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      <div className="text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {status === 'no-token'
              ? t('auth:parentalConsent.noTokenHeading')
              : t('auth:parentalConsent.errorHeading')}
          </h3>
          <p className="text-gray-600 mb-4">
            {status === 'no-token'
              ? t('auth:parentalConsent.noTokenBody')
              : t('auth:parentalConsent.errorBody')}
          </p>
        </div>

        <Link to="/login">
          <Button fullWidth variant="outline">
            {t('auth:parentalConsent.backToLogin')}
          </Button>
        </Link>
      </div>
    </AuthLayout>
  );
};

export default ParentalConsentPage;
